/**
 * <event-console-edit> — the console's edit destination. A routed component
 * in the console's own nested `event-console` area (see dashboard.view.ts's
 * header for why).
 *
 * Mounts <event-form> verbatim. On close: if the event no longer exists
 * (deleted from the form), the console itself has nothing left to show, so
 * this bubbles `event-deleted` past the console for the shell to handle
 * (composed:true crosses the console's own shadow boundary); otherwise it
 * returns to the dashboard destination via the nested area, not a local
 * field, so the destination survives exactly like every other nav-rail move.
 */
import { area } from "@mhmo91/schmancy";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { when } from "lit/directives/when.js";
import "../event-form.view.ts";
import { canManageEvents, eventsState } from "../events.state.ts";
import { consoleDestination, consoleEventId } from "./console.state.ts";

@schmancyElement("event-console-edit")
export default class EventConsoleEdit extends SchmancyElement {
	private handleFormClose(): void {
		const eventId = consoleEventId.value;
		const stillExists = eventsState.value.some((e) => e.id === eventId);
		if (!stillExists) {
			this.dispatchEvent(
				new CustomEvent("event-deleted", { bubbles: true, composed: true }),
			);
			return;
		}
		area.push({
			...consoleDestination("event-console-dashboard"),
			historyStrategy: "replace",
		});
	}

	override render(): TemplateResult {
		const eventId = consoleEventId.value;
		return html`
			${when(
				canManageEvents.get() && eventId,
				(id) =>
					html`<event-form .id=${id} @close=${() => this.handleFormClose()}></event-form>`,
			)}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-console-edit": EventConsoleEdit;
	}
}
